import { useState } from 'react';

import { Link, Users } from 'lucide-react';
import { toast } from 'sonner';

import { DocumentDownloadOptions } from '../../../../../../../../../backend/src/types/types';
import { useUserContext } from '../../../../../../../Contexts/User';
import useDownloadAnswer from '../../../hooks/useDownloadAnswer';
import useGetThreadStatus from '../../../hooks/useGetThreadStatus';
import useTogglePublishedThread from '../../../hooks/useTogglePublishedThread';
import ConfidenceChip from '../ConfidenceChip';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Copy } from '@/components/atoms/Copy';
import Download from '@/components/atoms/Download';
import { Button } from '@/components/atoms/new-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/Tooltip';
import { ThreadExportProgressModal } from '@/components/molecules/ThreadExportProgressModal';
import ThreadDownloadModal from '@/components/organisms/ThreadDownloadModal';
import { cleanReferencesAndCopyToClipboard } from '@/constants/referencing';
import { Artifact, ConfidenceLevelObject, KimMode, ThreadMessage } from '@/types';

interface AnswerHeaderProps {
  confidenceLevel: ConfidenceLevelObject;
  answerMessage: ThreadMessage | undefined;
  setShowConfidenceTooltip: (show: boolean) => void;
  caseId: string;
  threadId: string;
  artifact: Artifact | undefined;
  flowType: KimMode | undefined;
}

const AnswerHeader: React.FC<AnswerHeaderProps> = ({
  confidenceLevel,
  answerMessage,
  setShowConfidenceTooltip,
  caseId,
  threadId,
  artifact,
  flowType,
}) => {
  const [downloadStart, setDownloadStart] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  const messageId = answerMessage?.id || '';
  const { downloadAnswer, downloadAnswerWithDocuments } = useDownloadAnswer(caseId, threadId, messageId, artifact);
  const { user } = useUserContext();

  // Get current thread data to check isPublished status
  const { data: threadData } = useGetThreadStatus({ caseId, threadId, enabled: true });
  const { mutate: toggleThreadPublished, isLoading: isToggling } = useTogglePublishedThread(caseId);

  const handleDownloadClick = () => {
    // If there's an artifact, download directly without showing modal
    if (artifact) {
      handleDownloadAnswer();
    } else {
      // Show modal for formatting options
      setShowDownloadModal(true);
    }
  };

  const handleDownloadAnswer = async (options?: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
    trackCustomEvent(TrackingEvents.DownloadKimAnswer, { caseId, threadId, includeSourceDocuments });
    setShowDownloadModal(false);

    if (includeSourceDocuments) {
      // Start async export job
      try {
        setExportError(undefined);
        const result = await downloadAnswer(options, true);

        if (result && 'exportExists' in result) {
          if (result.exportExists) {
            // Export already exists - download immediately
            await downloadAnswerWithDocuments();
          } else {
            // Job in progress or just started - show progress modal
            setIsCreatingExport(true);
            setShowExportProgressModal(true);
          }
        }
      } catch (error) {
        console.error('[AnswerHeader] Export failed:', error);
        setExportError(error instanceof Error ? error.message : 'Failed to start export');
        setShowExportProgressModal(true);
      }
    } else {
      // Original synchronous download
      setDownloadStart(true);

      try {
        await downloadAnswer(options);
      } catch (error) {
        console.error('Download failed:', error);
        toast.error('Download failed. Please try again.');
      } finally {
        // Reset state
        setDownloadStart(false);
        setDownloadComplete(true);
        setTimeout(() => setDownloadComplete(false), 1000);
      }
    }
  };

  const handleExportDownload = async () => {
    try {
      await downloadAnswerWithDocuments();
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    setShowExportProgressModal(false);
    setIsCreatingExport(false);
    setExportError(undefined);
  };

  const copyPageLinkToClipboard = () => {
    trackCustomEvent(TrackingEvents.CopyKimPageLink, { caseId, threadId });
    navigator.clipboard.writeText(window.location.href);
    toast.info('Link to page copied to clipboard');
  };

  const handleTogglePublished = () => {
    if (!threadData?.isPublished) {
      trackCustomEvent(TrackingEvents.KimThreadPublished, { caseId, threadId });
    }
    toggleThreadPublished(threadId, {
      onSuccess: ({ isPublished }) => {
        toast.success(`Thread ${isPublished ? 'published' : 'unpublished'} successfully`);
      },
      onError: () => {
        toast.error('Failed to toggle thread published status');
      },
    });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        {confidenceLevel.text !== 'Low' && (
          <>
            <Button variant="outline" size="sm" className="text-xs" onClick={copyPageLinkToClipboard}>
              <Link className="h-3 w-3" />
            </Button>
            {flowType !== 'extraction_flow' && (
              <Copy
                textSize="xs"
                variant="button"
                copyHandler={() => {
                  trackCustomEvent(TrackingEvents.CopyKimAnswer, { caseId, threadId });
                  cleanReferencesAndCopyToClipboard(answerMessage?.content);
                  toast.success('Copied to clipboard');
                }}
              />
            )}

            <Download
              textSize="xs"
              handleDownload={handleDownloadClick}
              downloadStart={downloadStart}
              downloadComplete={downloadComplete}
              variant="button"
            />
          </>
        )}
        {threadData?.createdById === user?.id && (
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`text-xs ${threadData?.isPublished ? 'border-blue-200 bg-blue-50 text-blue-700' : ''}`}
                onClick={handleTogglePublished}
                disabled={isToggling}
              >
                <Users className="h-3 w-3" />
                <p className="text-xs">{threadData?.isPublished ? 'Unpublish' : 'Publish'}</p>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-56">
              <p className="text-center">Publishing will add it to the shared list of threads</p>
            </TooltipContent>
          </Tooltip>
        )}
        <ConfidenceChip
          confidenceLevel={confidenceLevel}
          onClick={() => {
            trackCustomEvent(TrackingEvents.KimConfidenceLevel, { caseId, threadId });
            setShowConfidenceTooltip(true);
          }}
        />
      </div>
      <ThreadDownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadAnswer}
        isLoading={downloadStart}
      />
      <ThreadExportProgressModal
        isOpen={showExportProgressModal}
        onClose={handleCloseExportModal}
        caseId={caseId}
        threadId={threadId}
        messageId={messageId}
        onDownload={handleExportDownload}
        isCreating={isCreatingExport}
        error={exportError}
      />
    </>
  );
};

export default AnswerHeader;
